/*
 * hunting-seasons/app.js — страница /hunting-seasons: место (карта, поиск, GPS, список
 * регионов) + дата → кого можно добывать, с цитатой из Правил охоты; ООПТ в точке; документы
 * региона. Расчёт — engine.js (общий с node-тестом), данные — public/data/hunting-seasons.
 */
import * as E from './engine.js';
import { createRegions, myRegion, rememberPlace, rememberRegion } from '../regions/index.js';

const W = window.__HS || {};
const T = W.i18n || {};
const FED = W.fed || { species: [], groups: [], oopt: {} };
const SLUG = 'hunting-seasons';
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const MONTHS = T.months || [];
const MONTHS_SHORT = T.months_short || [];

function tr(key, rep = {}) {
    let s = T[key] ?? key;
    for (const [k, v] of Object.entries(rep)) s = s.split(':' + k).join(String(v));
    return s;
}
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const toast = (msg, type) => { try { window.__toast && window.__toast(msg, type || 'info'); } catch (e) { /* toast optional */ } };
const stage = (name, data) => { try { window.__stage && window.__stage(name, data || {}); } catch (e) { /* telemetry optional */ } };
const diag = (issue, data) => { try { window.__aeDiagnostic && window.__aeDiagnostic(Object.assign({ issue }, data || {})); } catch (e) { /* telemetry optional */ } };

function todayNum() { const d = new Date(); return E.dayNum(d.getFullYear(), d.getMonth() + 1, d.getDate()); }
/** «81 день», «3 дня», «12 дней» — формы из перевода (d1, d2, d5). */
function days(n) {
    const a = Math.abs(n) % 100, b = a % 10;
    const w = a > 10 && a < 20 ? T.d5 : b === 1 ? T.d1 : b >= 2 && b <= 4 ? T.d2 : T.d5;
    return `${n} ${w || ''}`.trim();
}
function dateText(n, withYear = true) { const p = E.fromDayNum(n); return `${p.d} ${MONTHS[p.m - 1]}${withYear ? ' ' + p.y : ''}`; }

// ── состояние ────────────────────────────────────────────────────────────
const S = { lat: null, lon: null, regionId: '', date: todayNum(), group: '', q: '', dogs: false, near: false, edgeKm: null, outside: false, abroad: '', via: '' };
let REG = null;               // regions.json
let REG_BY = new Map();       // id → region (+polys)
/* Где точка и контур субъекта — общий примитив регионов (docs/regions.md). */
const RG = createRegions(Object.assign({ locale: 'ru' }, W.regions || {}));
let mine = { last: null, profile: null };
const huntId = (codes) => (codes || []).find((c) => REG_BY.has(c)) || '';
let polysReady = null;        // Promise
const ooptCache = new Map();  // id → Promise<file|null>
const regionalCache = new Map();
let ooptHere = [];            // ООПТ в точке
let ooptState = '';           // '', 'loading', 'failed'
let userTouched = false;

async function getJson(url) {
    const r = await fetch(url, { credentials: 'omit' });
    if (!r.ok) throw new Error('HTTP ' + r.status);
    return r.json();
}

function loadRegions() {
    if (polysReady) return polysReady;
    polysReady = getJson(W.regionsUrl).then(async (reg) => {
        await Promise.all((reg.regions || []).map(async (r) => {
            const g = await RG.region('RUS', r.id);
            r.polys = g ? g.polys : null;
            r.code = g ? g.code : r.id;
        }));
        REG = reg;
        for (const r of reg.regions || []) REG_BY.set(r.id, r);
        return reg;
    }).catch((e) => {
        polysReady = null;
        diag('hs_data_load', { msg: String(e && e.message || e).slice(0, 80) });
        toast(tr('data_fail'), 'error');
        throw e;
    });
    return polysReady;
}

function loadOopt(id) {
    if (!ooptCache.has(id)) {
        ooptCache.set(id, getJson(`${W.ooptBase}${id.toLowerCase()}.json?v=${W.dataV || ''}`).catch(() => { ooptCache.delete(id); return null; }));
    }
    return ooptCache.get(id);
}
function loadRegional(id) {
    const r = REG_BY.get(id);
    if (!r || !r.regional) return Promise.resolve(null);
    if (!regionalCache.has(id)) {
        regionalCache.set(id, getJson(`${W.regionalBase}${id.toLowerCase()}.json?v=${W.dataV || ''}`).catch(() => { regionalCache.delete(id); return null; }));
    }
    return regionalCache.get(id);
}

// ── место ────────────────────────────────────────────────────────────────
/* Место ставят несколько источников наперегонки: город профиля (поле места применяет его
   само при монтировании), ссылка, память. Ответ, пришедший после более нового выбора, не
   пишет ни состояние, ни память: иначе точка ссылки уходила в память под именем города
   профиля. */
let placeSeq = 0;
async function setPoint(lat, lon, src, placeName) {
    const seq = ++placeSeq;
    S.lat = +(+lat).toFixed(5); S.lon = +(+lon).toFixed(5);
    S.via = src === 'profile' ? 'profile' : '';
    if (src !== 'url') userTouched = true;
    let w;
    try { await loadRegions(); w = await RG.at(S.lat, S.lon, { a3: 'RUS', nearKm: 15 }); } catch (e) { return; }
    if (seq !== placeSeq) return;
    // Точка за границей — страна называется, а «мой регион» предлагается кнопками (renderAlerts).
    S.regionId = w.region ? huntId(w.region.codes) : '';
    S.outside = !S.regionId;
    S.abroad = !w.region && w.country ? w.country.name : '';
    S.near = !!(w.region && !w.region.exact);
    S.edgeKm = w.region ? w.region.edgeKm : null;
    if (src !== 'url' && src !== 'profile') {
        rememberPlace({ lat: S.lat, lon: S.lon, name: placeName || '' });
        if (w.region) rememberRegion({ code: w.region.code, a3: 'RUS', name: w.region.name });
    }
    $('#hs-region').value = S.regionId;
    ooptHere = [];
    render();
    drawMarker();
    if (S.regionId) {
        ooptState = 'loading';
        renderAlerts();
        const [file] = await Promise.all([loadOopt(S.regionId), loadRegional(S.regionId)]);
        if (seq !== placeSeq) return;
        ooptState = file ? '' : 'failed';
        ooptHere = file ? E.ooptAt(S.lon, S.lat, file) : [];
        drawOopt(file);
        render();
    }
    complete(src);
}

async function setRegion(id, src) {
    const seq = ++placeSeq;
    S.lat = null; S.lon = null; S.near = false; S.edgeKm = null; S.outside = false; S.abroad = ''; ooptHere = []; ooptState = '';
    S.regionId = id || '';
    // auto-* — открыто само (память, профиль): говорится вслух, но это не выбор человека.
    const auto = /^auto-/.test(src || ''), via = String(src || '').replace(/^(auto|mine)-/, '');
    S.via = via === 'profile' || via === 'last' ? via : '';
    if (src !== 'url' && !auto) userTouched = true;
    if (S.regionId) {
        try { await loadRegions(); } catch (e) { return; }
        if (seq !== placeSeq) return;
        const r = REG_BY.get(S.regionId);
        if (r && src !== 'url' && !auto) rememberRegion({ code: r.code, a3: 'RUS', name: r.name });
        await loadRegional(S.regionId);
        loadOopt(S.regionId).then((f) => { if (S.regionId === id) drawOopt(f); });
    } else drawOopt(null);
    render();
    drawMarker();
    fitRegion();
    complete(src);
}

function complete(src) {
    // Ссылка, город профиля, который поле места применяет само, и регион из памяти — не результат.
    if (src === 'url' || src === 'default' || src === 'profile' || /^auto-/.test(src || '')) return;
    try {
        window.__aeToolComplete && window.__aeToolComplete({ src, region: S.regionId || 'none', point: S.lat !== null, oopt: ooptHere.length ? ooptHere[0].c : 'none', ahead: Math.round((S.date - todayNum()) / 7) });
    } catch (e) { /* telemetry optional */ }
}

// ── расчёт ───────────────────────────────────────────────────────────────
function regionObj() { const r = REG_BY.get(S.regionId); return r ? { id: r.id, fd: r.fd } : null; }
function visibleSpecies() {
    const q = S.q.trim().toLowerCase();
    return FED.species.filter((sp) => {
        if (S.group && sp.group !== S.group) return false;
        if (q && !(`${sp.name} ${sp.kw || ''}`.toLowerCase().includes(q))) return false;
        return true;
    });
}
function withDogs(sp) {
    return S.dogs ? sp : Object.assign({}, sp, { seasons: sp.seasons.filter((s) => s.kind !== 'dogs') });
}
function evaluate() {
    const reg = regionObj();
    const regional = regionalCache.has(S.regionId) ? null : null;
    void regional;
    const out = [];
    for (const sp of FED.species) {
        const st = E.speciesStatus(withDogs(sp), S.date, reg, currentRegional());
        out.push({ sp, st });
    }
    return out;
}
let REGIONAL_NOW = null;
function currentRegional() { return REGIONAL_NOW; }
async function refreshRegional() {
    REGIONAL_NOW = S.regionId ? await loadRegional(S.regionId) : null;
}

// ── отрисовка ────────────────────────────────────────────────────────────
function boundText(b, y) {
    if (b && typeof b === 'object') {
        const n = E.nthSaturday(y, b.month, b.nthSat);
        return `${b.nthSat === 2 ? tr('sat2') : tr('sat3')} (${tr('in_year', { y, date: dateText(n, false) })})`;
    }
    return null;
}
function seasonLine(row) {
    const s = row.season;
    const kind = s.kind || 'season';
    let who = s.who ? s.who : (kind !== 'season' ? tr('kind_' + kind) : '');
    if (row.source === 'regional' && row.regional && row.regional.zone) who = `${who}${who ? ', ' : ''}${row.regional.zone}`;
    let dates;
    if (row.source === 'regional' && row.regional) {
        const occ = row.current || row.next;
        dates = occ ? tr('range', { from: dateText(occ.from, false), to: dateText(occ.to) }) : '';
    } else {
        const occ = row.current || row.next;
        if (occ) dates = tr('range', { from: dateText(occ.from, false), to: dateText(occ.to) });
        if (s.type === 'window') {
            const extra = [];
            if (s.minDays) extra.push(tr('min_days', { n: s.minDays }));
            if (s.maxDays) extra.push(tr('max_days', { n: s.maxDays }));
            if (extra.length) dates += ', ' + extra.join(', ');
        }
    }
    // Даты строки — это уже текущий (или ближайший) срок; хвост говорит только «сколько».
    let state;
    if (row.state === 'open') state = `<span class="hs-row-s open">· ${esc(tr('left', { n: days(row.current.to - S.date + 1) }))}</span>`;
    else if (row.state === 'window') state = `<span class="hs-row-s window">· ${esc(tr('window_left', { n: days(row.current.to - S.date + 1) }))}</span>`;
    // Указ региона этот сезон не открывает: дат нет — строка без двоеточия.
    else if (row.state === 'banned') return `<li><span class="hs-row-k">${esc(who)}</span> <span class="hs-row-s">— ${esc(tr('banned_row'))}</span></li>`;
    else state = row.next ? `<span class="hs-row-s">· ${esc(tr('opens_in', { n: days(row.next.from - S.date) }))}</span>` : '';
    return `<li><span class="hs-row-k">${esc(who ? who + ': ' : '')}</span>${esc(dates || '')} ${state}</li>`;
}
function sourceBlock(st) {
    const seen = new Set(), parts = [];
    for (const row of st.rows) {
        const q = row.source === 'regional' && row.regional ? row.regional.quote : row.season.quote;
        const ref = row.source === 'regional' && row.regional ? (row.regional.ref || '') : row.season.ref;
        const key = q + '|' + ref;
        if (!q || seen.has(key)) continue;
        seen.add(key);
        const who = row.source === 'regional' ? tr('regional_src') : tr('federal_src');
        const zone = row.source === 'regional' && row.regional && row.regional.zone_note ? `<p>${esc(row.regional.zone)}: ${esc(row.regional.zone_note)}</p>` : '';
        parts.push(`<blockquote>${esc(q)}</blockquote><p>${esc(who)}, ${esc(ref)}</p>${zone}`);
    }
    if (!parts.length) return '';
    return `<details class="hs-src"><summary>${esc(tr('show_src'))}</summary>${parts.join('')}</details>`;
}
function pill(status) {
    const key = { open: 'st_open', zones: 'st_zones', window: 'st_window', closed: 'st_closed', banned: 'st_banned' }[status] || 'st_closed';
    return `<span class="hs-pill ${status}">${esc(tr(key))}</span>`;
}

function render() {
    const results = evaluate();
    const vis = new Set(visibleSpecies().map((s) => s.id));
    renderAnswer(results);
    renderAlerts();
    renderList(results.filter((r) => vis.has(r.sp.id)));
    renderCalendar(results.filter((r) => vis.has(r.sp.id)));
    renderDocs();
    syncUrl();
}

function renderAnswer(results) {
    const r = REG_BY.get(S.regionId);
    // Точка вне России — это и есть ответ: крупно, первым, с выходом к своему региону.
    const away = S.lat !== null && S.outside;
    const head = S.abroad ? tr('abroad_head', { country: S.abroad }) : tr('sea_head');
    $('#hs-answer-v').textContent = away ? head : r ? tr('answer_place', { date: dateText(S.date), region: r.name }) : tr('answer_date', { date: dateText(S.date) });
    $('#hs-answer-sub').textContent = away ? tr('noplace_sub') : r ? tr('permit') : tr('no_place');
    $('#hs-answer-go').innerHTML = away ? mineButtons(true) : '';
    $('#hs-stats').hidden = away;
    const c = { open: 0, window: 0, closed: 0 };
    for (const { st } of results) {
        const k = st.status === 'banned' ? 'closed' : st.status === 'zones' ? 'window' : st.status;
        c[k] = (c[k] || 0) + 1;
    }
    $('#hs-live-v').textContent = away ? head : `${tr('sum_open')} ${c.open} · ${tr('st_window')} ${c.window}${r ? ' · ' + r.name : ''}`;
    $('#hs-stats').innerHTML =
        `<div class="tc-stat good"><div class="tc-stat-l">${esc(tr('sum_open'))}</div><div class="tc-stat-v">${c.open}</div></div>` +
        `<div class="tc-stat warn"><div class="tc-stat-l">${esc(tr('sum_window'))}</div><div class="tc-stat-v">${c.window}</div></div>` +
        `<div class="tc-stat"><div class="tc-stat-l">${esc(tr('sum_closed'))}</div><div class="tc-stat-v">${c.closed}</div></div>`;
}

/* «Мой регион»: кнопки к региону города из профиля и к прошлому выбору (без повторов) и
   выход к списку регионов — путь вперёд, когда точка вне России. */
function mineButtons(primary) {
    const seen = new Set([S.regionId]);
    const out = [];
    for (const [cand, key, src] of [[mine.profile, 'mine_profile', 'profile'], [mine.last, 'mine_last', 'last']]) {
        const id = cand ? huntId(cand.codes) : '';
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(`<button type="button" class="tc-btn${primary && !out.length ? '' : ' ghost'}" data-mine="${esc(src)}" data-id="${esc(id)}">${esc(tr(key, { region: REG_BY.get(id).name }))}</button>`);
    }
    out.push(`<button type="button" class="tc-btn ghost" data-mine="list">${esc(tr('pick_from_list'))}</button>`);
    return `<div class="tc-actions hs-mt">${out.join('')}</div>`;
}

function renderAlerts() {
    const box = $('#hs-alerts');
    const msgs = [];
    const r = REG_BY.get(S.regionId);
    if (r && S.via === 'profile') msgs.push(['', esc(tr('from_profile', { region: r.name, city: (mine.profile && mine.profile.place) || '' }))]);
    else if (r && S.via === 'last') msgs.push(['', esc(tr('from_last', { region: r.name }))]);
    if (r && S.near) msgs.push(['warn', esc(tr('near_coast', { region: r.name }))]);
    else if (r && S.edgeKm !== null && S.edgeKm < 3) msgs.push(['warn', esc(tr('near_edge', { km: S.edgeKm.toFixed(1).replace('.', ','), region: r.name }))]);
    if (r && !r.fd) msgs.push(['warn', esc(tr('no_fd', { region: r.name }))]);
    if (S.lat !== null && r) {
        if (ooptState === 'loading') msgs.push(['', esc(tr('oopt_loading'))]);
        else if (ooptState === 'failed') msgs.push(['warn', esc(tr('oopt_failed'))]);
        else if (ooptHere.length) {
            for (const f of ooptHere.slice(0, 3)) {
                const cat = FED.oopt[f.c] || {};
                const name = f.n || tr('oopt_unnamed');
                const head = tr(cat.verdict === 'ban' ? 'oopt_ban' : cat.verdict === 'limit' ? 'oopt_limit' : 'oopt_check', { name, cat: (cat.name || '').toLowerCase() });
                const say = cat.say ? `<br>${esc(cat.say)}` : '';
                const quote = cat.quote ? `<details class="hs-src"><summary>${esc(tr('show_src'))}</summary><blockquote>${esc(cat.quote)}</blockquote><p>${esc(cat.ref || '')}</p></details>` : `<p class="tc-note">${esc(cat.ref || '')}</p>`;
                msgs.push([cat.verdict === 'ban' ? 'bad' : 'warn', `<b>${esc(head)}</b>${say}${quote}`]);
            }
            msgs.push(['', `<span class="tc-note">${esc(tr('oopt_source'))}</span>`]);
        } else if (ooptState === '') msgs.push(['', esc(tr('oopt_none'))]);
    }
    box.innerHTML = msgs.map(([k, h]) => `<div class="tc-msg ${k}">${h}</div>`).join('');
}

function renderList(rows) {
    const box = $('#hs-list');
    if (!rows.length) { box.innerHTML = `<p class="tc-empty">${esc(tr('nothing'))}</p>`; return; }
    const html = [];
    const reg = regionObj();
    for (const g of FED.groups) {
        const items = rows.filter((x) => x.sp.group === g.id && x.st.rows.length);
        if (!items.length) continue;
        let note = g.note;
        html.push(`<section><h3 class="hs-group-h">${esc(g.name)}</h3><p class="hs-group-note">${esc(note)}${g.id === 'waterfowl' && (!reg || ['yufo', 'skfo'].includes(reg.fd)) ? ' ' + esc(tr('south_note')) : ''}</p>`);
        for (const { sp, st } of items) {
            html.push(`<div class="hs-sp"><div class="hs-sp-top"><span class="hs-sp-name">${esc(sp.name)}</span>${pill(st.status)}</div><ul class="hs-rows">${st.rows.map(seasonLine).join('')}</ul>${sourceBlock(st)}</div>`);
        }
        html.push('</section>');
    }
    box.innerHTML = html.join('');
}

function renderCalendar(rows) {
    const box = $('#hs-cal');
    const y = E.fromDayNum(S.date).y;
    const jan1 = E.dayNum(y, 1, 1), dec31 = E.dayNum(y, 12, 31), span = dec31 - jan1 + 1;
    const pct = (n) => ((n - jan1) / span) * 100;
    const html = [`<div class="hs-cal-row hs-cal-head"><span></span><div class="hs-cal-track">${MONTHS_SHORT.map((m) => `<span>${esc(m)}</span>`).join('')}</div></div>`];
    const now = pct(S.date);
    const reg = regionObj();
    for (const { sp } of rows) {
        const bars = [];
        const regional = currentRegional();
        for (const s of withDogs(sp).seasons) {
            if (!E.applies(s, reg)) continue;
            const regs = E.regionalFor(regional, sp.id, s.kind || 'season', s).filter((r) => !r.banned);
            const spans = [];
            for (const yy of [y - 1, y]) {
                if (regs.length) {
                    for (const r of regs) { const o = E.regionalOccurrence(r, yy); if (o) spans.push({ o, win: false, zone: r.zone }); }
                } else spans.push({ o: E.occurrence(s, yy), win: s.type === 'window', zone: '' });
            }
            for (const { o, win, zone } of spans) {
                const a = Math.max(o.from, jan1), b = Math.min(o.to, dec31);
                if (b < a) continue;
                const label = `${s.who || tr('kind_' + (s.kind || 'season'))}${zone ? ', ' + zone : ''}: ${dateText(o.from)} — ${dateText(o.to)}`;
                bars.push(`<i class="hs-cal-bar${win ? ' win' : ''}" style="left:${pct(a).toFixed(2)}%;width:${Math.max(0.6, pct(b + 1) - pct(a)).toFixed(2)}%" title="${esc(label)}"></i>`);
            }
        }
        if (!bars.length) continue;
        html.push(`<div class="hs-cal-row"><span class="hs-cal-name" title="${esc(sp.name)}">${esc(sp.name)}</span><div class="hs-cal-track" role="img" aria-label="${esc(sp.name)}">${bars.join('')}<i class="hs-cal-now" style="left:${now.toFixed(2)}%" title="${esc(tr('today_mark'))}"></i></div></div>`);
    }
    box.innerHTML = html.length > 1 ? html.join('') : `<p class="tc-empty">${esc(tr('nothing'))}</p>`;
}

function actLine(a) {
    if (!a) return '';
    const p = a.date ? a.date.split('-').reverse().join('.') : '';
    const text = tr('act_line', { who: a.who || '', date: p, num: a.number || '', title: a.title || '' });
    const link = a.eo ? ` — <a href="http://publication.pravo.gov.ru/document/${encodeURIComponent(a.eo)}" target="_blank" rel="noopener">${esc(tr('docs_official'))}</a>` : '';
    return esc(text) + link;
}
function renderDocs() {
    const box = $('#hs-docs');
    const r = REG_BY.get(S.regionId);
    if (!r) { box.innerHTML = `<p class="tc-hint">${esc(tr('docs_pick'))}</p>`; return; }
    const p = (r.acts || {}).params, s = (r.acts || {}).scheme;
    const out = [`<h4>${esc(r.name)}</h4>`];
    out.push(`<p><b>${esc(tr('docs_params'))}:</b> ${p && p.base ? actLine(p.base) : esc(tr('docs_none_params'))}</p>`);
    if (p && p.changes && p.changes.length) out.push(`<p class="tc-note">${tr('docs_last', { act: actLine(p.changes[p.changes.length - 1]) })}</p>`);
    if (p && p.limits && p.limits.length) out.push(`<p class="tc-note">${esc(tr('docs_limits'))}</p><ul>${p.limits.slice().reverse().map((a) => `<li>${actLine(a)}</li>`).join('')}</ul>`);
    out.push(`<p><b>${esc(tr('docs_scheme'))}:</b> ${s && s.base ? actLine(s.base) : esc(tr('docs_none_scheme'))}</p>`);
    if (s && s.changes && s.changes.length) out.push(`<p class="tc-note">${tr('docs_last', { act: actLine(s.changes[s.changes.length - 1]) })}</p>`);
    const regional = currentRegional();
    out.push(`<p class="tc-note">${regional ? esc(tr('docs_regional_yes', { act: regional.act || '' })) : esc(tr('docs_regional_no'))}</p>`);
    if (!regional && r.note) out.push(`<div class="tc-msg warn">${esc(tr(r.note.status === 'disputed' ? 'cover_disputed' : 'cover_notfound'))}: ${esc(r.note.reason)}</div>`);
    box.innerHTML = out.join('');
}

// ── карта ────────────────────────────────────────────────────────────────
let map = null, marker = null, regionLayer = null, ooptLayer = null, ooptFor = '';
const COLORS = { zap: '#dc2626', np: '#ea580c', zak: '#ca8a04', pp: '#7c3aed', pam: '#7c3aed', vosp: '#7c3aed' };
function initMap() {
    if (map || !window.L) return;
    const Lf = window.L;
    map = Lf.map('hs-map', { zoomControl: true, worldCopyJump: true, attributionControl: true }).setView([60, 90], 3);
    const tile = W.tile || {};
    if (window.__mapTiles) {
        window.__mapTiles.base(Lf, map, null, { slug: SLUG, style: tile.style, styles: tile.styles, labels: tile.labels, text: tile.offline, credit: 'ООПТ © OpenStreetMap', opts: { maxZoom: 18 } });
    } else if (window.__basemapFallback) {
        window.__basemapFallback(Lf, map, '');
    }
    map.on('click', (e) => setPoint(e.latlng.lat, e.latlng.lng, 'map'));
    drawMarker();
    if (S.regionId) { loadOopt(S.regionId).then(drawOopt); fitRegion(); }
}
function drawMarker() {
    if (!map) return;
    const Lf = window.L;
    if (marker) { marker.remove(); marker = null; }
    if (S.lat !== null) {
        marker = Lf.marker([S.lat, S.lon], { keyboard: true }).addTo(map);
        if (!map.getBounds().contains([S.lat, S.lon]) || map.getZoom() < 8) map.setView([S.lat, S.lon], Math.max(map.getZoom(), 10), { animate: false });
    }
    drawRegion();
}
function drawRegion() {
    if (!map) return;
    const Lf = window.L;
    if (regionLayer) { regionLayer.remove(); regionLayer = null; }
    const r = REG_BY.get(S.regionId);
    if (!r || !r.polys) return;
    const latlngs = r.polys.map((p) => p.map((ring) => ring.map(([x, y]) => [y, x])));
    regionLayer = Lf.polygon(latlngs, { color: '#2563eb', weight: 2, fill: false, interactive: false }).addTo(map);
}
function fitRegion() {
    if (!map) return;
    const r = REG_BY.get(S.regionId);
    if (!r || !r.polys || S.lat !== null) return;
    let a = 90, b = 180, c = -90, d = -180;
    for (const p of r.polys) for (const [x, y] of p[0]) { if (y < a) a = y; if (x < b) b = x; if (y > c) c = y; if (x > d) d = x; }
    if (d - b > 300) { b = 150; d = 190; }   // Чукотка через 180-й меридиан
    map.fitBounds([[a, b], [c, d]], { padding: [10, 10] });
}
function drawOopt(file) {
    if (!map) return;
    const Lf = window.L;
    if (ooptLayer && ooptFor === (file && file.region)) return;
    if (ooptLayer) { ooptLayer.remove(); ooptLayer = null; ooptFor = ''; }
    if (!file || !file.f || !file.f.length) return;
    const group = Lf.layerGroup();
    for (const f of file.f) {
        if (!f._polys) f._polys = E.decodeRings(f.g, file.q || 1e4);
        const cat = FED.oopt[f.c] || {};
        const col = COLORS[f.c] || '#7c3aed';
        const latlngs = f._polys.map((p) => p.map((ring) => ring.map(([x, y]) => [y, x])));
        const poly = Lf.polygon(latlngs, { color: col, weight: 1.5, fillColor: col, fillOpacity: f.c === 'zap' ? 0.28 : 0.18, bubblingMouseEvents: true });
        poly.bindTooltip(`${esc(f.n || tr('oopt_unnamed'))} — ${esc((cat.name || '').toLowerCase())}`, { sticky: true });
        group.addLayer(poly);
    }
    group.addTo(map);
    ooptLayer = group; ooptFor = file.region;
}

// Карта покрытия: где даты региона уже в расчёте, а где — окна Правил.
let coverLayer = null;
async function toggleCoverage(btn) {
    const on = btn.getAttribute('aria-pressed') !== 'true';
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    const note = $('#hs-cover-note');
    if (!on) { if (coverLayer) { coverLayer.remove(); coverLayer = null; } note.hidden = true; return; }
    try { await loadRegions(); } catch (e) { return; }
    if (!map) initMap();
    if (!map) return;
    const Lf = window.L;
    const group = Lf.layerGroup();
    let n = 0;
    for (const r of REG.regions) {
        if (!r.polys) continue;
        if (r.regional) n++;
        const disputed = !r.regional && r.note && r.note.status === 'disputed';
        const col = r.regional ? '#16a34a' : disputed ? '#d97706' : '#94a3b8';
        const latlngs = r.polys.map((p) => p.map((ring) => ring.map(([x, y]) => [y, x])));
        const poly = Lf.polygon(latlngs, { color: col, weight: 1, fillColor: col, fillOpacity: r.regional ? 0.3 : disputed ? 0.25 : 0.06 });
        const say = r.regional ? tr('docs_regional_yes', { act: '' }).replace(/[:.\s]+$/, '') : r.note ? tr(r.note.status === 'disputed' ? 'cover_disputed' : 'cover_notfound') : tr('docs_regional_no');
        poly.bindTooltip(`${esc(r.name)} — ${esc(say)}`, { sticky: true });
        group.addLayer(poly);
    }
    coverLayer = group.addTo(map);
    map.setView([60, 90], 3);
    note.textContent = tr('cover_legend', { n });
    note.hidden = false;
}

// ── URL и управление ─────────────────────────────────────────────────────
function syncUrl() {
    try {
        const u = new URL(location.href);
        ['lat', 'lon', 'region', 'date'].forEach((k) => u.searchParams.delete(k));
        if (S.lat !== null) { u.searchParams.set('lat', S.lat); u.searchParams.set('lon', S.lon); }
        else if (S.regionId) u.searchParams.set('region', S.regionId);
        if (S.date !== todayNum()) u.searchParams.set('date', E.isoOf(S.date));
        if (userTouched) history.replaceState(history.state, '', u.toString());
    } catch (e) { /* URL sync optional */ }
}
function setDate(n) {
    if (n === null || Number.isNaN(n)) return;
    S.date = n;
    $('#hs-date').value = E.isoOf(n);
    render();
}

async function boot() {
    const p = new URLSearchParams(location.search);
    const urlDate = E.parseIso(p.get('date'));
    if (urlDate !== null) S.date = urlDate;
    $('#hs-date').value = E.isoOf(S.date);
    $('#hs-date').addEventListener('change', (e) => { const n = E.parseIso(e.target.value); if (n !== null) { userTouched = true; setDate(n); } });
    $('#hs-today').addEventListener('click', () => { userTouched = true; setDate(todayNum()); });
    $('#hs-week').addEventListener('click', () => { userTouched = true; setDate(todayNum() + 7); });
    $('#hs-region').addEventListener('change', async (e) => { await setRegion(e.target.value, 'select'); await refreshRegional(); render(); });
    $$('#hs-groups [data-group]').forEach((b) => b.addEventListener('click', () => {
        S.group = b.dataset.group;
        $$('#hs-groups [data-group]').forEach((x) => { const on = x === b; x.classList.toggle('on', on); x.setAttribute('aria-pressed', on ? 'true' : 'false'); });
        render();
    }));
    let qTimer = 0;
    $('#hs-q').addEventListener('input', (e) => { clearTimeout(qTimer); qTimer = setTimeout(() => { S.q = e.target.value; render(); }, 120); });
    const dogs = $('#hs-dogs');
    try { if (window.__toolSettings) window.__toolSettings.bind(SLUG, dogs, 'dogs', false); } catch (e) { /* settings optional */ }
    S.dogs = !!dogs.checked;
    dogs.addEventListener('change', () => { S.dogs = dogs.checked; render(); });
    $('#hs-gps').addEventListener('click', () => {
        if (!navigator.geolocation) { toast(tr('geo_fail'), 'error'); return; }
        navigator.geolocation.getCurrentPosition(async (pos) => { await setPoint(pos.coords.latitude, pos.coords.longitude, 'gps'); await refreshRegional(); render(); },
            (err) => { toast(tr('geo_fail'), 'error'); diag('hs_geo_fail', { code: err && err.code }); },
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
    });
    $('#hs-cover').addEventListener('click', (e) => toggleCoverage(e.currentTarget));
    $('#hs-live').addEventListener('click', () => { const pnl = $('#hs-answer-panel'); if (pnl) pnl.scrollIntoView({ block: 'nearest' }); });
    $('#hs-share').addEventListener('click', async () => {
        userTouched = true; syncUrl();
        try { await navigator.clipboard.writeText(location.href); toast(tr('copied'), 'success'); } catch (e) { toast(tr('copy_fail'), 'error'); }
    });
    $('#hs-pdf').addEventListener('click', async () => {
        if (!window.__generatePDF) { toast(tr('data_fail'), 'error'); return; }
        const r = REG_BY.get(S.regionId);
        const name = `${tr('pdf_name')}-${E.isoOf(S.date)}.pdf`;
        await window.__generatePDF({
            element: $('#hs .tc-stage'), filename: name, title: tr('pdf_title', { region: r ? r.name : '—', date: dateText(S.date) }),
            mode: ['css', 'legacy'],
            onclone: (doc) => { doc.querySelectorAll('.hs-map-panel,#hs-share,#hs-pdf,#hs-docs-panel').forEach((n) => n.remove()); doc.querySelectorAll('details.hs-src').forEach((d) => d.removeAttribute('open')); },
        });
    });
    if (window.LocationInput) {
        window.LocationInput.mount($('#hs-loc'), {
            tool: SLUG, initial: {}, t: W.loc || {},
            onChange: async (loc) => { await setPoint(loc.lat, loc.lng, loc.source || 'city', loc.cityName || loc.name); await refreshRegional(); render(); },
        });
    }
    $('#hs-answer-go').addEventListener('click', async (e) => {
        const b = e.target.closest && e.target.closest('[data-mine]');
        if (!b) return;
        if (b.dataset.mine === 'list') { const sel = $('#hs-region'); sel.scrollIntoView({ block: 'nearest' }); sel.focus(); try { if (sel.showPicker) sel.showPicker(); } catch (x) { /* not every browser opens a select by script */ } return; }
        try { window.__ae && window.__ae('feature_use', { slug: SLUG, action: 'mine', source: b.dataset.mine }); } catch (x) { /* telemetry optional */ }
        $('#hs-region').value = b.dataset.id;
        await setRegion(b.dataset.id, 'mine-' + b.dataset.mine); await refreshRegional(); render();
    });

    render();
    const mapEl = $('#hs-map');
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => { if (es.some((x) => x.isIntersecting)) { io.disconnect(); loadRegions().then(initMap, () => initMap()); } }, { rootMargin: '300px' });
        io.observe(mapEl);
    } else loadRegions().then(initMap, () => initMap());

    try { mine = await myRegion(RG, 'RUS'); } catch (e) { /* no memory — the person picks */ }
    const lat = parseFloat(p.get('lat')), lon = parseFloat(p.get('lon'));
    let src = 'default';
    if (Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180) {
        src = 'url-point';
        await setPoint(lat, lon, 'url'); await refreshRegional(); render();
    } else if (p.get('region')) {
        src = 'url-region';
        try { await loadRegions(); } catch (e) { /* toast already shown */ }
        if (REG_BY.has(p.get('region'))) { await setRegion(p.get('region'), 'url'); await refreshRegional(); render(); }
    } else {
        // Без места в ссылке — свой регион человека: выбранный последним на сайте, иначе город
        // профиля (его точку поле места уже поставило само — тогда ничего не трогаем). Откуда
        // место взялось, страница говорит вслух (renderAlerts).
        try { await loadRegions(); } catch (e) { /* toast already shown */ }
        const profileApplied = !!(window.LocationInput && window.__USER_LOCATION);
        const pick = mine.last ? ['auto-last', mine.last] : mine.profile && !profileApplied ? ['auto-profile', mine.profile] : null;
        const id = pick ? huntId(pick[1].codes) : '';
        if (id) {
            src = pick[0];
            $('#hs-region').value = id;
            await setRegion(id, pick[0]); await refreshRegional(); render();
        }
    }
    stage('ready', { src });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
